import React, { useState } from 'react';
import { Modal, Tabs, Tab, Form, Row, Col, Button, Spinner, Alert } from 'react-bootstrap';
import { FaUser, FaHome, FaGlobe, FaPlus, FaTrash, FaFileExcel, FaFilePdf } from 'react-icons/fa';

const SettingsModal = ({
  show, onHide, t,
  passwordForm, setPasswordForm, handleChangePassword, passwordLoading,
  emailForm, setEmailForm, handleEmailChange, emailLoading,
  deleteForm, setDeleteForm, handleDeleteAccount, deleteLoading,
  onExportExcel, onExportPdf,
  roomList, containerList, saveSettings,
  language, setLanguage, currency, setCurrency, location, setLocation,
}) => {
  const [newRoom, setNewRoom] = useState('');
  const [newContainer, setNewContainer] = useState('');

  const addRoom = () => {
    const v = newRoom.trim();
    if (v) { saveSettings('rooms', [...roomList, v]); setNewRoom(''); }
  };

  const addContainer = () => {
    const v = newContainer.trim();
    if (v) { saveSettings('containers', [...containerList, v]); setNewContainer(''); }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{t.settingsTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <Tabs defaultActiveKey="account" fill className="px-3 pt-2">

          {/* ── Tab 1: Account ── */}
          <Tab eventKey="account" title={<><FaUser className="me-1" />{t.settingsTabAccount}</>}>
            <div className="p-3">

              {/* Change Password */}
              <h6 className="fw-bold mb-2">{t.accountSecurity}</h6>
              <Form onSubmit={handleChangePassword} className="mb-4">
                <Row className="g-2">
                  <Col md={4}>
                    <Form.Control size="sm" type="password" placeholder={t.currentPassword} required
                      value={passwordForm.current}
                      onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })} />
                  </Col>
                  <Col md={4}>
                    <Form.Control size="sm" type="password" placeholder={t.newPassword} required
                      value={passwordForm.next}
                      onChange={(e) => setPasswordForm({ ...passwordForm, next: e.target.value })} />
                  </Col>
                  <Col md={4}>
                    <Form.Control size="sm" type="password" placeholder={t.confirmNew} required
                      value={passwordForm.confirm}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })} />
                  </Col>
                  <Col xs={12}>
                    <Button size="sm" type="submit" disabled={passwordLoading}>
                      {passwordLoading ? <Spinner size="sm" animation="border" className="me-1" /> : null}
                      {t.update}
                    </Button>
                    <span className="text-muted small ms-2">{t.passwordComplexityHint}</span>
                  </Col>
                </Row>
              </Form>

              {/* Change Email */}
              <h6 className="fw-bold mb-2">{t.updateEmail}</h6>
              <Form onSubmit={handleEmailChange} className="mb-4">
                <Row className="g-2 align-items-end">
                  <Col md={5}>
                    <Form.Label className="small mb-1">{t.newEmailAddress}</Form.Label>
                    <Form.Control size="sm" type="email" required
                      value={emailForm.next}
                      onChange={(e) => setEmailForm({ ...emailForm, next: e.target.value })} />
                  </Col>
                  <Col md={5}>
                    <Form.Label className="small mb-1">{t.currentPassword}</Form.Label>
                    <Form.Control size="sm" type="password" required autoComplete="off"
                      value={emailForm.password}
                      onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })} />
                  </Col>
                  <Col md={2}>
                    <Button size="sm" type="submit" disabled={emailLoading} className="w-100">
                      {emailLoading ? <Spinner size="sm" animation="border" /> : t.change}
                    </Button>
                  </Col>
                </Row>
              </Form>

              {/* Data Export */}
              <h6 className="fw-bold mb-2">{t.dataExport}</h6>
              <div className="d-flex gap-2 mb-4">
                <Button variant="success" size="sm" onClick={onExportExcel}>
                  <FaFileExcel className="me-1" />{t.export}
                </Button>
                <Button variant="danger" size="sm" onClick={onExportPdf}>
                  <FaFilePdf className="me-1" />{t.exportPdf}
                </Button>
              </div>

              {/* Delete Account */}
              <Alert variant="danger" className="mb-0">
                <h6 className="fw-bold mb-1">{t.deleteAccount}</h6>
                <p className="small mb-2">{t.deleteAccountWarning}</p>
                <Form onSubmit={handleDeleteAccount}>
                  <Row className="g-2">
                    <Col md={4}>
                      <Form.Control size="sm" type="password" placeholder={t.yourPassword} required
                        value={deleteForm.password}
                        onChange={(e) => setDeleteForm({ ...deleteForm, password: e.target.value })} />
                    </Col>
                    <Col md={5}>
                      <Form.Control size="sm" type="text" placeholder={t.typeDeleteToConfirm} required
                        value={deleteForm.confirm}
                        onChange={(e) => setDeleteForm({ ...deleteForm, confirm: e.target.value })} />
                    </Col>
                    <Col md={3}>
                      <Button variant="danger" size="sm" type="submit" className="w-100" disabled={deleteLoading}>
                        {deleteLoading ? <Spinner size="sm" animation="border" /> : t.eraseMyAccount}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Alert>
            </div>
          </Tab>

          {/* ── Tab 2: Rooms & Storage ── */}
          <Tab eventKey="rooms" title={<><FaHome className="me-1" />{t.settingsTabRooms}</>}>
            <div className="p-3">
              <Row>
                <Col md={6}>
                  <h6 className="fw-bold">{t.roomList}</h6>
                  <div className="d-flex mb-2 gap-1">
                    <Form.Control size="sm" placeholder={t.addRoom} value={newRoom}
                      onChange={(e) => setNewRoom(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRoom())} />
                    <Button variant="success" size="sm" onClick={addRoom}><FaPlus /></Button>
                  </div>
                  <ul className="list-group">
                    {roomList.map(r => (
                      <li key={r} className="list-group-item d-flex justify-content-between align-items-center py-1 small">
                        {r}
                        <Button variant="link" className="text-danger p-0"
                          onClick={() => saveSettings('rooms', roomList.filter(item => item !== r))}>
                          <FaTrash size={10} />
                        </Button>
                      </li>
                    ))}
                  </ul>
                </Col>
                <Col md={6}>
                  <h6 className="fw-bold">{t.containerList}</h6>
                  <div className="d-flex mb-2 gap-1">
                    <Form.Control size="sm" placeholder={t.addContainer} value={newContainer}
                      onChange={(e) => setNewContainer(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addContainer())} />
                    <Button variant="success" size="sm" onClick={addContainer}><FaPlus /></Button>
                  </div>
                  <ul className="list-group">
                    {containerList.map(c => (
                      <li key={c} className="list-group-item d-flex justify-content-between align-items-center py-1 small">
                        {c}
                        <Button variant="link" className="text-danger p-0"
                          onClick={() => saveSettings('containers', containerList.filter(item => item !== c))}>
                          <FaTrash size={10} />
                        </Button>
                      </li>
                    ))}
                  </ul>
                </Col>
              </Row>
            </div>
          </Tab>

          {/* ── Tab 3: Location & Currency ── */}
          <Tab eventKey="location" title={<><FaGlobe className="me-1" />{t.settingsTabLocation}</>}>
            <div className="p-3">
              <Row className="g-4">
                <Col md={4}>
                  <Form.Label className="fw-semibold">{t.language}</Form.Label>
                  <Form.Select value={language}
                    onChange={(e) => { setLanguage(e.target.value); saveSettings('language', e.target.value); }}>
                    <option value="en">English</option>
                    <option value="fr">Français</option>
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-semibold">{t.currency}</Form.Label>
                  <Form.Control type="text" value={currency} placeholder="CAD"
                    onChange={(e) => setCurrency(e.target.value)}
                    onBlur={() => saveSettings('currency', currency)} />
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-semibold">{t.location}</Form.Label>
                  <Form.Control type="text" value={location} placeholder="Canada"
                    onChange={(e) => setLocation(e.target.value)}
                    onBlur={() => saveSettings('location', location)} />
                </Col>
              </Row>
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
    </Modal>
  );
};

export default SettingsModal;
